import { IonInfiniteScroll } from '@ionic/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'use-debounce'
import {
  NumberParam,
  useQueryParam,
  withDefault,
  StringParam,
} from 'use-query-params'
import { ProgressBlock } from '../Loaders'
import { SkeletonList } from '../SkeletonList'
import {
  SCROLLABLE_LIST_DEFAULT_VALUES,
  SCROLLABLE_LIST_PAGE_KEY,
  SCROLLABLE_LIST_SORT_KEY,
  DEFAULT_PAGE,
  DEBOUNCE_DELAY,
} from './constants'
import { FetchFn, Item, ListData } from './types'

type ScrollableListProps<Row extends Item> = {
  queryKey: string
  pageSizeParams?: number
  skeletonHeight?: number
  skeletonNode?: ReactNode
  skeletonItemMarginBottom?: string
  fetchFn: FetchFn<Row>
  externalFilters?: { key: string; value: string }[]
  empty?: ReactNode
  mapItems: (items: Row[]) => ReactNode
}

export function ScrollableList<Row extends Item>({
  pageSizeParams = 10,
  skeletonHeight,
  skeletonNode,
  skeletonItemMarginBottom,
  fetchFn,
  externalFilters,
  empty,
  mapItems,
  queryKey,
}: ScrollableListProps<Row>) {
  const queryClient = useQueryClient()
  const infiniteScrollRef = useRef<HTMLIonInfiniteScrollElement | null>(null)
  const isMounted = useRef<boolean>(true)
  const isOnInfinite = useRef<boolean>(false)
  const [isShowSkeleton, toggleIsShowSkeleton] = useState(false)

  const [pageQuery, setPageQuery] = useQueryParam(
    SCROLLABLE_LIST_PAGE_KEY,
    withDefault(NumberParam, DEFAULT_PAGE),
  )

  const [sortQuery] = useQueryParam(
    SCROLLABLE_LIST_SORT_KEY,
    withDefault(StringParam, ''),
  )

  const memoExternalFiltersKey = useMemo(
    () => (externalFilters || []).map(({ value }) => value).join(','),
    [externalFilters],
  )

  const memoGridKey = useMemo(
    () => [queryKey, sortQuery, pageQuery, memoExternalFiltersKey],
    [queryKey, sortQuery, pageQuery, memoExternalFiltersKey],
  )

  const [debounceGridKey] = useDebounce(memoGridKey, DEBOUNCE_DELAY)

  const pageSize = useMemo(
    () =>
      isMounted.current && pageQuery
        ? pageQuery * pageSizeParams
        : pageSizeParams,
    [pageQuery, pageSizeParams],
  )

  const getPreviousData = () => {
    if (pageQuery <= DEFAULT_PAGE) return undefined

    const previousPageKey = [
      queryKey,
      sortQuery,
      pageQuery - 1,
      memoExternalFiltersKey,
    ]
    return queryClient.getQueryData<ListData<Row>>(previousPageKey)
  }

  const { isLoading, data } = useQuery(
    debounceGridKey,
    async () => {
      const page = isMounted.current && pageQuery ? undefined : pageQuery

      const { items, currentPage, lastPage, to } = await fetchFn(
        page,
        pageSize,
        externalFilters?.length ? externalFilters : [],
        sortQuery,
      )

      const previousData = getPreviousData()

      return {
        items: uniqBy([...(previousData?.items || []), ...(items || [])], 'id'),
        currentPage,
        lastPage,
        to,
      }
    },
    {
      keepPreviousData: true,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: async () => {
        isMounted.current = false
        isOnInfinite.current = false
        toggleIsShowSkeleton(false)
        infiniteScrollRef.current?.complete()
      },
      onError: async () => {
        isMounted.current = false
        isOnInfinite.current = false
        toggleIsShowSkeleton(false)
        infiniteScrollRef.current?.complete()
      },
    },
  )

  const {
    items = [],
    to,
    currentPage,
    lastPage,
  } = useMemo(() => data || SCROLLABLE_LIST_DEFAULT_VALUES, [data])

  const handlePageChange = (pageParams?: number) => {
    setPageQuery(pageParams ?? pageQuery + 1, 'replaceIn')
  }

  const filtrationByExternalFilter = () => {
    if (isMounted.current) return
    handlePageChange(DEFAULT_PAGE)
    toggleIsShowSkeleton(true)
  }

  useEffect(() => {
    filtrationByExternalFilter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoExternalFiltersKey, sortQuery])

  if (isLoading || isShowSkeleton)
    return (
      <SkeletonList
        count={pageSize}
        skeletonHeight={skeletonHeight}
        skeletonNode={skeletonNode}
        itemMarginBottom={skeletonItemMarginBottom}
      />
    )

  if (!to) {
    return (
      <>{empty || <div style={{ fontSize: '18px' }}>Нечего не найдено</div>}</>
    )
  }

  return (
    <>
      <div>{mapItems(items)}</div>

      <IonInfiniteScroll
        key={`${currentPage}-${lastPage}`}
        threshold='30%'
        ref={infiniteScrollRef}
        onIonInfinite={() => {
          if (isOnInfinite.current) return
          handlePageChange()
          isOnInfinite.current = true
        }}
        disabled={currentPage >= lastPage}
      >
        <ProgressBlock height={32} size={28} />
      </IonInfiniteScroll>
    </>
  )
}
